import { z } from 'zod';
import { CountryCodeSchema } from './geo';
import { PhoneSchema } from './auth';
import { BusinessTypeSchema, FoodTypeSchema } from './restaurants';

/* ------------------------------------------------------------------ */
/* Market Initialers (W20)                                             */
/*                                                                     */
/* Field people (realm "mi") who walk a town, log restaurant prospects, */
/* visit them (GPS-gated), and hand interested owners over to W4        */
/* onboarding. MI Leads (staff role "mi_lead", town-scoped) run the     */
/* roster, beats (H3 res 9 hex sets), the prospect table, the funnel,   */
/* hand-offs and weekly payouts inside Capsico OS at /mi-lead.          */
/*                                                                     */
/*   /v1/mi/*       the MI app (realm mi, device-bound phone OTP)       */
/*   /v1/mi-lead/*  Capsico OS staff with mi.view / mi.manage           */
/*                                                                     */
/* Money is always in minor units (paise / paisa).                      */
/* ------------------------------------------------------------------ */

/** H3 resolution used for beats. */
export const MI_BEAT_H3_RES = 9;

/** A beat hex cell id (H3 index string, res 9). */
export const H3CellSchema = z
  .string()
  .trim()
  .regex(/^[0-9a-f]{15}$/, 'Invalid map cell');

const isoDate = z.iso.date('Use a date like 2026-11-01');
const isoDateTime = z.iso.datetime({ offset: true });
const opId = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_:.-]{8,80}$/, 'Invalid op id');
const note = z.string().trim().max(500);

export const MiPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type MiPoint = z.infer<typeof MiPointSchema>;

export const MiFixSchema = MiPointSchema.extend({
  /** Phone-reported accuracy in metres. */
  accuracyM: z.number().min(0).max(5000).nullable().optional(),
});
export type MiFix = z.infer<typeof MiFixSchema>;

/* ---------------- prospects ---------------- */

export const PROSPECT_STATUSES = [
  'new',
  'visited',
  'interested',
  'docs_pending',
  'handed_off',
  'live',
  'not_interested',
  'closed',
  'duplicate',
] as const;
export const ProspectStatusSchema = z.enum(PROSPECT_STATUSES);
export type ProspectStatus = z.infer<typeof ProspectStatusSchema>;

export const PROSPECT_STATUS_LABELS: Record<ProspectStatus, string> = {
  new: 'New',
  visited: 'Visited',
  interested: 'Interested',
  docs_pending: 'Docs pending',
  handed_off: 'Handed off',
  live: 'Live',
  not_interested: 'Not interested',
  closed: 'Closed',
  duplicate: 'Duplicate',
};

/** Statuses a field visit still makes sense for (open pipeline). */
export const OPEN_PROSPECT_STATUSES: readonly ProspectStatus[] = [
  'new',
  'visited',
  'interested',
  'docs_pending',
];

export const PROSPECT_SOURCES = ['osm', 'mi', 'ops'] as const;
export const ProspectSourceSchema = z.enum(PROSPECT_SOURCES);
export type ProspectSource = z.infer<typeof ProspectSourceSchema>;

export const SERVICE_MODES = ['dine_in', 'takeaway', 'delivery'] as const;
export const ServiceModeSchema = z.enum(SERVICE_MODES);
export type ServiceMode = z.infer<typeof ServiceModeSchema>;

export const PROSPECT_PHOTO_KINDS = ['storefront', 'menu'] as const;
export const ProspectPhotoKindSchema = z.enum(PROSPECT_PHOTO_KINDS);
export type ProspectPhotoKind = z.infer<typeof ProspectPhotoKindSchema>;

export const MI_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const MAX_MI_PHOTO_BYTES = 8 * 1024 * 1024;

export const ProspectFieldsSchema = z.object({
  name: z.string().trim().min(2).max(80),
  cuisines: z.array(z.string().trim().min(2).max(40)).max(8).default([]),
  phone: PhoneSchema.nullable().optional(),
  ownerName: z.string().trim().min(2).max(80).nullable().optional(),
  address: z.string().trim().min(3).max(300).nullable().optional(),
  /** Average price for two, minor units. */
  avgPriceForTwoMinor: z.number().int().min(0).max(10_000_00).nullable().optional(),
  /** Free text, e.g. Zomato, Swiggy, none. */
  currentPlatforms: z.array(z.string().trim().min(2).max(40)).max(6).default([]),
  serviceModes: z.array(ServiceModeSchema).max(3).default([]),
  notes: note.nullable().optional(),
  followUpOn: isoDate.nullable().optional(),
});
export type ProspectFields = z.infer<typeof ProspectFieldsSchema>;

/** MI logs a new place. A storefront photo (uploaded first) is required. */
export const NewProspectInputSchema = ProspectFieldsSchema.extend({
  opId,
  townId: z.uuid(),
  location: MiFixSchema,
  /** Storage keys returned by POST /v1/mi/photos/upload-url, already PUT. */
  photos: z
    .array(z.object({ key: z.string().min(10).max(300), kind: ProspectPhotoKindSchema }))
    .min(1, 'Add a storefront photo')
    .max(6),
  /** Set after the app showed the duplicate warning and the MI chose to save anyway. */
  confirmNotDuplicate: z.boolean().default(false),
});
export type NewProspectInput = z.infer<typeof NewProspectInputSchema>;

export const UpdateProspectInputSchema = ProspectFieldsSchema.partial()
  .extend({ expectedVersion: z.number().int().min(1) })
  .refine(
    (v) => Object.keys(v).some((key) => key !== 'expectedVersion'),
    'Send at least one field to change',
  );
export type UpdateProspectInput = z.infer<typeof UpdateProspectInputSchema>;

export const ProspectPhotoSchema = z.object({
  id: z.uuid(),
  kind: ProspectPhotoKindSchema,
  /** Short-lived signed link (private bucket). */
  url: z.string(),
  createdAt: z.string(),
});
export type ProspectPhoto = z.infer<typeof ProspectPhotoSchema>;

export const DuplicateMatchSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  status: ProspectStatusSchema,
  reason: z.enum(['phone', 'near_similar_name']),
  distanceM: z.number().nullable(),
  similarity: z.number().nullable(),
});
export type DuplicateMatch = z.infer<typeof DuplicateMatchSchema>;

export const ProspectRowSchema = z.object({
  id: z.uuid(),
  townId: z.uuid(),
  townName: z.string(),
  name: z.string(),
  cuisines: z.array(z.string()),
  status: ProspectStatusSchema,
  source: ProspectSourceSchema,
  /** Lists always mask the number. */
  phoneMasked: z.string().nullable(),
  address: z.string().nullable(),
  location: MiPointSchema,
  assignedMiId: z.uuid().nullable(),
  assignedMiName: z.string().nullable(),
  followUpOn: z.string().nullable(),
  lastVisitAt: z.string().nullable(),
  possibleDuplicateOf: z.uuid().nullable(),
  /** Metres from the caller's position, when it was sent. */
  distanceM: z.number().nullable(),
  updatedAt: z.string(),
  version: z.number().int(),
});
export type ProspectRow = z.infer<typeof ProspectRowSchema>;

export const ProspectListSchema = z.object({
  rows: z.array(ProspectRowSchema),
  counts: z.partialRecord(ProspectStatusSchema, z.number().int()),
  nextCursor: z.string().nullable(),
});
export type ProspectList = z.infer<typeof ProspectListSchema>;

export const VISIT_OUTCOMES = [
  'owner_absent',
  'call_back',
  'interested',
  'docs_pending',
  'not_interested',
  'closed_permanently',
] as const;
export const VisitOutcomeSchema = z.enum(VISIT_OUTCOMES);
export type VisitOutcome = z.infer<typeof VisitOutcomeSchema>;

export const VISIT_OUTCOME_LABELS: Record<VisitOutcome, string> = {
  owner_absent: 'Owner not there',
  call_back: 'Asked to come back',
  interested: 'Interested',
  docs_pending: 'Collecting documents',
  not_interested: 'Not interested',
  closed_permanently: 'Closed for good',
};

/** Why the MI logged a visit outside the radius. */
export const FAR_REASONS = [
  'gps_poor',
  'pin_wrong',
  'met_elsewhere',
  'prospect_moved',
  'other',
] as const;
export const FarReasonSchema = z.enum(FAR_REASONS);
export type FarReason = z.infer<typeof FarReasonSchema>;

export const LogVisitInputSchema = z.object({
  opId,
  location: MiFixSchema,
  /** When the visit happened on the phone (offline visits sync later). */
  at: isoDateTime,
  outcome: VisitOutcomeSchema,
  nextStep: z.string().trim().max(200).nullable().optional(),
  followUpOn: isoDate.nullable().optional(),
  notes: note.nullable().optional(),
  /** Needed when the MI is further than mi.visit_radius_m from the prospect. */
  farReason: FarReasonSchema.nullable().optional(),
  farNote: z.string().trim().max(200).nullable().optional(),
  /** When the MI fixed the prospect's pin at this visit (moves the prospect here). */
  movePin: z.boolean().default(false),
});
export type LogVisitInput = z.infer<typeof LogVisitInputSchema>;

export const VisitRowSchema = z.object({
  id: z.uuid(),
  miId: z.uuid(),
  miName: z.string().nullable(),
  at: z.string(),
  outcome: VisitOutcomeSchema,
  nextStep: z.string().nullable(),
  notes: z.string().nullable(),
  distanceM: z.number(),
  withinRadius: z.boolean(),
  farReason: FarReasonSchema.nullable(),
});
export type VisitRow = z.infer<typeof VisitRowSchema>;

export const VisitResultSchema = z.object({
  visit: VisitRowSchema,
  prospectStatus: ProspectStatusSchema,
  /** True when the same op id was already stored (offline replay). */
  replayed: z.boolean(),
});
export type VisitResult = z.infer<typeof VisitResultSchema>;

export const ProspectEventSchema = z.object({
  id: z.number().int(),
  kind: z.string(),
  actor: z.string(),
  at: z.string(),
  detail: z.record(z.string(), z.unknown()),
});
export type ProspectEvent = z.infer<typeof ProspectEventSchema>;

export const ProspectDetailSchema = ProspectRowSchema.extend({
  /** The full number for the MI who works it and for Leads. */
  phone: z.string().nullable(),
  ownerName: z.string().nullable(),
  avgPriceForTwoMinor: z.number().int().nullable(),
  currentPlatforms: z.array(z.string()),
  serviceModes: z.array(ServiceModeSchema),
  notes: z.string().nullable(),
  osmRef: z.string().nullable(),
  outletId: z.uuid().nullable(),
  handoffId: z.uuid().nullable(),
  photos: z.array(ProspectPhotoSchema),
  visits: z.array(VisitRowSchema),
  events: z.array(ProspectEventSchema),
  duplicates: z.array(DuplicateMatchSchema),
  country: CountryCodeSchema,
});
export type ProspectDetail = z.infer<typeof ProspectDetailSchema>;

export const CreatedProspectSchema = z.object({
  prospect: ProspectDetailSchema,
  replayed: z.boolean(),
});
export type CreatedProspect = z.infer<typeof CreatedProspectSchema>;

export const DupCheckQuerySchema = z.object({
  townId: z.uuid(),
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  name: z.string().trim().min(2).max(80),
  phone: PhoneSchema.optional(),
});
export type DupCheckQuery = z.infer<typeof DupCheckQuerySchema>;

export const MiProspectQuerySchema = z.object({
  status: ProspectStatusSchema.optional(),
  townId: z.uuid().optional(),
  q: z.string().trim().max(80).optional(),
  /** "mine" = assigned to or logged by me; "near" = sort by distance from lat/lng. */
  scope: z.enum(['mine', 'town', 'near']).default('mine'),
  lat: z.coerce.number().min(-90).max(90).optional(),
  lng: z.coerce.number().min(-180).max(180).optional(),
  followUpDue: z.coerce.boolean().optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type MiProspectQuery = z.infer<typeof MiProspectQuerySchema>;

export const StartMiPhotoUploadInputSchema = z.object({
  kind: ProspectPhotoKindSchema,
  contentType: z.enum(MI_PHOTO_CONTENT_TYPES),
  sizeBytes: z.number().int().min(1).max(MAX_MI_PHOTO_BYTES, 'Photos can be at most 8 MB'),
});
export type StartMiPhotoUploadInput = z.infer<typeof StartMiPhotoUploadInputSchema>;

export const MiPhotoUploadTargetSchema = z.object({
  key: z.string(),
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type MiPhotoUploadTarget = z.infer<typeof MiPhotoUploadTargetSchema>;

export const AttachPhotoInputSchema = z.object({
  key: z.string().min(10).max(300),
  kind: ProspectPhotoKindSchema,
});
export type AttachPhotoInput = z.infer<typeof AttachPhotoInputSchema>;

/* ---------------- hand-off to W4 onboarding ---------------- */

export const HANDOFF_STATUSES = ['started', 'live', 'offboarded', 'cancelled'] as const;
export const HandoffStatusSchema = z.enum(HANDOFF_STATUSES);
export type HandoffStatus = z.infer<typeof HandoffStatusSchema>;

export const HandOffInputSchema = z.object({
  opId,
  legalName: z.string().trim().min(2).max(120),
  displayName: z.string().trim().min(2).max(80).optional(),
  businessType: BusinessTypeSchema,
  taxRegistered: z.boolean(),
  ownerName: z.string().trim().min(2).max(80),
  ownerPhone: PhoneSchema,
  address: z.string().trim().min(5).max(300),
  landmark: z.string().trim().min(2).max(120).nullable().optional(),
  cuisines: z.array(z.string().trim().min(2).max(40)).min(1).max(8),
  foodType: FoodTypeSchema,
});
export type HandOffInput = z.infer<typeof HandOffInputSchema>;

export const HandoffRowSchema = z.object({
  id: z.uuid(),
  prospectId: z.uuid(),
  prospectName: z.string(),
  townId: z.uuid(),
  townName: z.string(),
  miId: z.uuid(),
  miName: z.string().nullable(),
  /** Who gets the pay (the logger unless a Lead reassigned it). */
  attributedMiId: z.uuid(),
  attributedMiName: z.string().nullable(),
  restaurantId: z.uuid().nullable(),
  outletId: z.uuid().nullable(),
  outletStatus: z.string().nullable(),
  status: HandoffStatusSchema,
  menuScanIds: z.array(z.uuid()),
  deliveredOrders: z.number().int(),
  createdAt: z.string(),
  liveAt: z.string().nullable(),
  offboardedAt: z.string().nullable(),
  /** "Pay per live", "Bonus", "Clawback" already credited. */
  earnedMinor: z.number().int(),
  version: z.number().int(),
});
export type HandoffRow = z.infer<typeof HandoffRowSchema>;

export const HandoffListSchema = z.object({ rows: z.array(HandoffRowSchema) });
export type HandoffList = z.infer<typeof HandoffListSchema>;

export const MenuScanUploadInputSchema = z.object({
  fileName: z.string().trim().min(1).max(120),
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']),
  sizeBytes: z
    .number()
    .int()
    .min(1)
    .max(20 * 1024 * 1024, 'Files can be at most 20 MB'),
});
export type MenuScanUploadInput = z.infer<typeof MenuScanUploadInputSchema>;

export const MenuScanTicketSchema = z.object({
  scanId: z.uuid(),
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type MenuScanTicket = z.infer<typeof MenuScanTicketSchema>;

export const MenuScanStateSchema = z.object({ scanId: z.uuid(), status: z.string() });
export type MenuScanState = z.infer<typeof MenuScanStateSchema>;

/* ---------------- duty + sessions ---------------- */

export const DutySessionSchema = z.object({
  id: z.uuid(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  distanceM: z.number(),
  pings: z.number().int(),
  lastPingAt: z.string().nullable(),
});
export type DutySession = z.infer<typeof DutySessionSchema>;

export const DutyStartInputSchema = z.object({
  opId,
  location: MiFixSchema.nullable().optional(),
});
export type DutyStartInput = z.infer<typeof DutyStartInputSchema>;

export const DutyStopInputSchema = z.object({
  opId,
  location: MiFixSchema.nullable().optional(),
});
export type DutyStopInput = z.infer<typeof DutyStopInputSchema>;

/** Heartbeat every 2 minutes while on duty (offline samples arrive in one batch). */
export const DutyPingInputSchema = z.object({
  sessionId: z.uuid(),
  samples: z
    .array(MiFixSchema.extend({ at: isoDateTime }))
    .min(1)
    .max(200),
});
export type DutyPingInput = z.infer<typeof DutyPingInputSchema>;

export const DutyPingResultSchema = z.object({
  /** Samples stored (repeats of an already stored timestamp are dropped). */
  accepted: z.number().int(),
  /** Session distance so far, metres. */
  distanceM: z.number(),
});
export type DutyPingResult = z.infer<typeof DutyPingResultSchema>;

export const DupCheckResultSchema = z.object({ rows: z.array(DuplicateMatchSchema) });
export type DupCheckResult = z.infer<typeof DupCheckResultSchema>;

export const DayMetricsSchema = z.object({
  date: z.string(),
  visits: z.number().int(),
  newProspects: z.number().int(),
  interested: z.number().int(),
  handedOff: z.number().int(),
  distanceM: z.number(),
  dutyMinutes: z.number().int(),
});
export type DayMetrics = z.infer<typeof DayMetricsSchema>;

export const QuotaProgressSchema = z.object({
  target: z.number().int(),
  done: z.number().int(),
  remaining: z.number().int(),
  pct: z.number(),
  met: z.boolean(),
});
export type QuotaProgress = z.infer<typeof QuotaProgressSchema>;

export const RouteStopSchema = z.object({
  prospectId: z.uuid(),
  name: z.string(),
  status: ProspectStatusSchema,
  location: MiPointSchema,
  reason: z.enum(['assigned', 'follow_up', 'beat_new']),
  /** Metres from the previous stop (or from the start point). */
  legM: z.number(),
  order: z.number().int(),
});
export type RouteStop = z.infer<typeof RouteStopSchema>;

export const MiTodaySchema = z.object({
  date: z.string(),
  quota: QuotaProgressSchema,
  metrics: DayMetricsSchema,
  route: z.array(RouteStopSchema),
  routeTotalM: z.number(),
  duty: DutySessionSchema.nullable(),
});
export type MiToday = z.infer<typeof MiTodaySchema>;

export const MiTodayQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90).optional(),
  lng: z.coerce.number().min(-180).max(180).optional(),
});
export type MiTodayQuery = z.infer<typeof MiTodayQuerySchema>;

/* ---------------- MI profile + bank ---------------- */

export const MiBankViewSchema = z.object({
  holder: z.string(),
  /** Only the last 4 digits are ever sent. */
  last4: z.string(),
  ifsc: z.string().nullable(),
  bankName: z.string().nullable(),
  branch: z.string().nullable(),
});
export type MiBankView = z.infer<typeof MiBankViewSchema>;

export const MiBankInputSchema = z.object({
  holder: z.string().trim().min(2).max(120),
  /** Leave out to keep the account on file. Never stored in form drafts. */
  accountNumber: z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z]{6,24}$/, 'Account numbers are 6–24 letters or digits')
    .optional(),
  ifsc: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'IFSC looks like HDFC0001234')
    .nullable()
    .optional(),
  bankName: z.string().trim().min(2).max(120).nullable().optional(),
  branch: z.string().trim().min(2).max(120).nullable().optional(),
});
export type MiBankInput = z.infer<typeof MiBankInputSchema>;

export const MiTownSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  country: CountryCodeSchema,
});
export type MiTown = z.infer<typeof MiTownSchema>;

export const MiSettingsViewSchema = z.object({
  dailyVisitQuota: z.number().int(),
  visitRadiusM: z.number().int(),
  duplicateRadiusM: z.number().int(),
  heartbeatSeconds: z.number().int(),
  payPerLiveMinor: z.number().int(),
  bonusMinor: z.number().int(),
  bonusOrders: z.number().int(),
  bonusWindowDays: z.number().int(),
  clawbackDays: z.number().int(),
  minPayoutMinor: z.number().int(),
});
export type MiSettingsView = z.infer<typeof MiSettingsViewSchema>;

export const MiMeSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  country: CountryCodeSchema,
  status: z.enum(['active', 'suspended']),
  towns: z.array(MiTownSchema),
  bank: MiBankViewSchema.nullable(),
  settings: MiSettingsViewSchema,
  duty: DutySessionSchema.nullable(),
  version: z.number().int(),
});
export type MiMe = z.infer<typeof MiMeSchema>;

/* ---------------- beats ---------------- */

export const BEAT_STATUSES = ['draft', 'active', 'archived'] as const;
export const BeatStatusSchema = z.enum(BEAT_STATUSES);
export type BeatStatus = z.infer<typeof BeatStatusSchema>;

export const BeatSchema = z.object({
  id: z.uuid(),
  townId: z.uuid(),
  name: z.string(),
  color: z.string(),
  status: BeatStatusSchema,
  miId: z.uuid().nullable(),
  miName: z.string().nullable(),
  cells: z.array(H3CellSchema),
  streets: z.array(z.string()),
  prospects: z.number().int(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type Beat = z.infer<typeof BeatSchema>;

export const BeatListSchema = z.object({ rows: z.array(BeatSchema) });
export type BeatList = z.infer<typeof BeatListSchema>;

const beatColor = z
  .string()
  .trim()
  .regex(/^#[0-9A-Fa-f]{6}$/, 'Use a colour like #F7B614');

export const CreateBeatInputSchema = z.object({
  townId: z.uuid(),
  name: z.string().trim().min(2).max(60),
  color: beatColor.optional(),
  cells: z.array(H3CellSchema).max(5000).default([]),
  streets: z.array(z.string().trim().min(2).max(120)).max(500).default([]),
  miId: z.uuid().nullable().optional(),
});
export type CreateBeatInput = z.infer<typeof CreateBeatInputSchema>;

export const UpdateBeatInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: z.string().trim().min(2).max(60).optional(),
    color: beatColor.optional(),
    cells: z.array(H3CellSchema).max(5000).optional(),
    streets: z.array(z.string().trim().min(2).max(120)).max(500).optional(),
    miId: z.uuid().nullable().optional(),
    status: BeatStatusSchema.optional(),
  })
  .refine(
    (v) => Object.keys(v).some((key) => key !== 'expectedVersion'),
    'Send at least one field to change',
  );
export type UpdateBeatInput = z.infer<typeof UpdateBeatInputSchema>;

/* ---------------- Lead: roster ---------------- */

export const MiRosterRowSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  status: z.enum(['invited', 'active', 'suspended']),
  country: CountryCodeSchema,
  towns: z.array(MiTownSchema),
  dailyQuota: z.number().int().nullable(),
  effectiveQuota: z.number().int(),
  onDuty: z.boolean(),
  lastPingAt: z.string().nullable(),
  visitsToday: z.number().int(),
  bankOnFile: z.boolean(),
  version: z.number().int(),
});
export type MiRosterRow = z.infer<typeof MiRosterRowSchema>;

export const MiRosterSchema = z.object({ rows: z.array(MiRosterRowSchema) });
export type MiRoster = z.infer<typeof MiRosterSchema>;

export const InviteMiInputSchema = z.object({
  name: z.string().trim().min(2).max(80),
  phone: PhoneSchema,
  townIds: z.array(z.uuid()).min(1).max(10),
  dailyQuota: z.number().int().min(1).max(200).nullable().optional(),
});
export type InviteMiInput = z.infer<typeof InviteMiInputSchema>;

export const UpdateMiInputSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: z.string().trim().min(2).max(80).optional(),
    townIds: z.array(z.uuid()).min(1).max(10).optional(),
    dailyQuota: z.number().int().min(1).max(200).nullable().optional(),
    status: z.enum(['active', 'suspended']).optional(),
  })
  .refine(
    (v) => Object.keys(v).some((key) => key !== 'expectedVersion'),
    'Send at least one field to change',
  );
export type UpdateMiInput = z.infer<typeof UpdateMiInputSchema>;

export const RosterQuerySchema = z.object({
  townId: z.uuid().optional(),
  status: z.enum(['invited', 'active', 'suspended']).optional(),
});
export type RosterQuery = z.infer<typeof RosterQuerySchema>;

/* ---------------- Lead: live map ---------------- */

export const LiveMiSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  sessionId: z.uuid(),
  startedAt: z.string(),
  lastPingAt: z.string().nullable(),
  location: MiPointSchema.nullable(),
  distanceM: z.number(),
  visitsToday: z.number().int(),
  /** No ping for 10 minutes or more. */
  stale: z.boolean(),
});
export type LiveMi = z.infer<typeof LiveMiSchema>;

export const LiveMiListSchema = z.object({ rows: z.array(LiveMiSchema), at: z.string() });
export type LiveMiList = z.infer<typeof LiveMiListSchema>;

export const LiveQuerySchema = z.object({ townId: z.uuid().optional() });

/* ---------------- Lead: prospects ---------------- */

export const LeadProspectQuerySchema = z.object({
  townId: z.uuid().optional(),
  status: ProspectStatusSchema.optional(),
  miId: z.uuid().optional(),
  q: z.string().trim().max(80).optional(),
  duplicatesOnly: z.coerce.boolean().optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
});
export type LeadProspectQuery = z.infer<typeof LeadProspectQuerySchema>;

export const ReassignProspectInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  miId: z.uuid().nullable(),
});
export type ReassignProspectInput = z.infer<typeof ReassignProspectInputSchema>;

export const MergeProspectInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  intoId: z.uuid(),
});
export type MergeProspectInput = z.infer<typeof MergeProspectInputSchema>;

export const CreateAssignmentsInputSchema = z.object({
  prospectIds: z.array(z.uuid()).min(1).max(500),
  miId: z.uuid(),
  dueOn: isoDate,
});
export type CreateAssignmentsInput = z.infer<typeof CreateAssignmentsInputSchema>;

/* ---------------- Lead: funnel ---------------- */

export const MiFunnelQuerySchema = z.object({
  townId: z.uuid().optional(),
  from: isoDate,
  to: isoDate,
  groupBy: z.enum(['mi', 'town']).default('mi'),
});
export type MiFunnelQuery = z.infer<typeof MiFunnelQuerySchema>;

export const MiFunnelRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  logged: z.number().int(),
  visited: z.number().int(),
  interested: z.number().int(),
  handedOff: z.number().int(),
  live: z.number().int(),
  visits: z.number().int(),
  farVisits: z.number().int(),
});
export type MiFunnelRow = z.infer<typeof MiFunnelRowSchema>;

export const MiFunnelSchema = z.object({
  rows: z.array(MiFunnelRowSchema),
  totals: MiFunnelRowSchema,
});
export type MiFunnel = z.infer<typeof MiFunnelSchema>;

export const HandoffQuerySchema = z.object({
  townId: z.uuid().optional(),
  status: HandoffStatusSchema.optional(),
});
export type HandoffQuery = z.infer<typeof HandoffQuerySchema>;

export const ReattributeInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  miId: z.uuid(),
});
export type ReattributeInput = z.infer<typeof ReattributeInputSchema>;

/* ---------------- pay: earnings ledger + payouts ---------------- */

export const MI_EARNING_KINDS = ['live', 'bonus', 'clawback', 'adjustment'] as const;
export const MiEarningKindSchema = z.enum(MI_EARNING_KINDS);
export type MiEarningKind = z.infer<typeof MiEarningKindSchema>;

export const MI_EARNING_LABELS: Record<MiEarningKind, string> = {
  live: 'Restaurant went live',
  bonus: '30-order bonus',
  clawback: 'Clawback (restaurant left early)',
  adjustment: 'Adjustment',
};

export const MiEarningRowSchema = z.object({
  id: z.uuid(),
  kind: MiEarningKindSchema,
  amountMinor: z.number().int(),
  country: CountryCodeSchema,
  prospectName: z.string().nullable(),
  handoffId: z.uuid().nullable(),
  note: z.string().nullable(),
  payoutId: z.uuid().nullable(),
  createdAt: z.string(),
});
export type MiEarningRow = z.infer<typeof MiEarningRowSchema>;

export const MI_PAYOUT_STATUSES = ['draft', 'approved', 'held', 'in_batch', 'paid'] as const;
export const MiPayoutStatusSchema = z.enum(MI_PAYOUT_STATUSES);
export type MiPayoutStatus = z.infer<typeof MiPayoutStatusSchema>;

export const MiPayoutRowSchema = z.object({
  id: z.uuid(),
  miId: z.uuid(),
  miName: z.string().nullable(),
  country: CountryCodeSchema,
  periodStart: z.string(),
  periodEnd: z.string(),
  amountMinor: z.number().int(),
  lines: z.number().int(),
  status: MiPayoutStatusSchema,
  holdReason: z.string().nullable(),
  bankReady: z.boolean(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  batchId: z.uuid().nullable(),
  paidAt: z.string().nullable(),
  reference: z.string().nullable(),
  version: z.number().int(),
});
export type MiPayoutRow = z.infer<typeof MiPayoutRowSchema>;

export const MiEarningsSchema = z.object({
  country: CountryCodeSchema,
  /** Credited lines not in a payout yet. */
  unpaidMinor: z.number().int(),
  lifetimeMinor: z.number().int(),
  lines: z.array(MiEarningRowSchema),
  payouts: z.array(MiPayoutRowSchema),
});
export type MiEarnings = z.infer<typeof MiEarningsSchema>;

export const MiPayoutQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  status: MiPayoutStatusSchema.optional(),
  periodStart: isoDate.optional(),
});
export type MiPayoutQuery = z.infer<typeof MiPayoutQuerySchema>;

export const MiPayoutListSchema = z.object({
  rows: z.array(MiPayoutRowSchema),
  counts: z.partialRecord(MiPayoutStatusSchema, z.number().int()),
  totals: z.object({ approvedMinor: z.number().int(), draftMinor: z.number().int() }),
});
export type MiPayoutList = z.infer<typeof MiPayoutListSchema>;

export const RunMiPayoutsInputSchema = z.object({
  country: CountryCodeSchema,
  /** The Monday of the week to settle; defaults to last week (local). */
  weekStart: isoDate.optional(),
});
export type RunMiPayoutsInput = z.infer<typeof RunMiPayoutsInputSchema>;

export const RunMiPayoutsResultSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  created: z.number().int(),
  skipped: z.number().int(),
  carried: z.number().int(),
});
export type RunMiPayoutsResult = z.infer<typeof RunMiPayoutsResultSchema>;

export const MiPayoutDecisionInputSchema = z.object({ expectedVersion: z.number().int().min(1) });
export type MiPayoutDecisionInput = z.infer<typeof MiPayoutDecisionInputSchema>;

export const MiPayoutHoldInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  hold: z.boolean(),
  reason: note.nullable().optional(),
});
export type MiPayoutHoldInput = z.infer<typeof MiPayoutHoldInputSchema>;

export const MiBatchRowSchema = z.object({
  id: z.uuid(),
  country: CountryCodeSchema,
  payouts: z.number().int(),
  totalMinor: z.number().int(),
  status: z.enum(['open', 'paid']),
  createdAt: z.string(),
  createdBy: z.string(),
  paidAt: z.string().nullable(),
  reference: z.string().nullable(),
});
export type MiBatchRow = z.infer<typeof MiBatchRowSchema>;

export const MiBatchListSchema = z.object({ rows: z.array(MiBatchRowSchema) });
export type MiBatchList = z.infer<typeof MiBatchListSchema>;

export const CreateMiBatchInputSchema = z.object({
  country: CountryCodeSchema,
  payoutIds: z.array(z.uuid()).min(1).max(500),
});
export type CreateMiBatchInput = z.infer<typeof CreateMiBatchInputSchema>;

export const MarkMiBatchPaidInputSchema = z.object({
  reference: z.string().trim().min(3, 'Add the bank reference').max(80),
});
export type MarkMiBatchPaidInput = z.infer<typeof MarkMiBatchPaidInputSchema>;

export const MiAdjustmentInputSchema = z.object({
  opId,
  miId: z.uuid(),
  amountMinor: z
    .number()
    .int()
    .min(-10_000_000)
    .max(10_000_000)
    .refine((value) => value !== 0, 'Enter an amount'),
  note: z.string().trim().min(3, 'Say why').max(300),
});
export type MiAdjustmentInput = z.infer<typeof MiAdjustmentInputSchema>;

/* ---------------- Lead: towns, Leads, settings ---------------- */

export const LeadTownsSchema = z.object({
  /** True for owners / admins / ops viewers: every town. */
  all: z.boolean(),
  towns: z.array(MiTownSchema),
});
export type LeadTowns = z.infer<typeof LeadTownsSchema>;

export const MiLeadRowSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  towns: z.array(MiTownSchema),
});
export type MiLeadRow = z.infer<typeof MiLeadRowSchema>;

export const MiLeadListSchema = z.object({ rows: z.array(MiLeadRowSchema) });
export type MiLeadList = z.infer<typeof MiLeadListSchema>;

export const SetLeadTownsInputSchema = z.object({ townIds: z.array(z.uuid()).max(50) });
export type SetLeadTownsInput = z.infer<typeof SetLeadTownsInputSchema>;

export const MiSettingsByCountrySchema = z.object({
  IN: MiSettingsViewSchema,
  NP: MiSettingsViewSchema,
});
export type MiSettingsByCountry = z.infer<typeof MiSettingsByCountrySchema>;

/** Settings keys W20 reads (all in CONFIG_CATALOGUE, group "mi"). */
export const MI_CONFIG_KEYS = {
  dailyVisitQuota: 'mi.daily_visit_quota',
  visitRadiusM: 'mi.visit_radius_m',
  duplicateRadiusM: 'mi.duplicate_radius_m',
  heartbeatSeconds: 'mi.heartbeat_seconds',
  payPerLive: 'mi.pay_per_live',
  bonus: 'mi.bonus_at_30_orders',
  bonusOrders: 'mi.bonus_orders',
  bonusWindowDays: 'mi.bonus_window_days',
  clawbackDays: 'mi.clawback_days',
  minPayout: 'mi.min_payout',
} as const;

/** Defaults when the settings registry is off (same as the catalogue seeds). */
export const MI_SETTING_DEFAULTS: Record<'IN' | 'NP', MiSettingsView> = {
  IN: {
    dailyVisitQuota: 25,
    visitRadiusM: 75,
    duplicateRadiusM: 30,
    heartbeatSeconds: 120,
    payPerLiveMinor: 30_000,
    bonusMinor: 20_000,
    bonusOrders: 30,
    bonusWindowDays: 60,
    clawbackDays: 14,
    minPayoutMinor: 10_000,
  },
  NP: {
    dailyVisitQuota: 25,
    visitRadiusM: 75,
    duplicateRadiusM: 30,
    heartbeatSeconds: 120,
    payPerLiveMinor: 48_000,
    bonusMinor: 32_000,
    bonusOrders: 30,
    bonusWindowDays: 60,
    clawbackDays: 14,
    minPayoutMinor: 16_000,
  },
};

/* ---------------- OSM import (script) ---------------- */

/** One food POI from an Overpass JSON extract (node or way centre). */
export const OsmElementSchema = z.object({
  type: z.enum(['node', 'way', 'relation']),
  id: z.number().int(),
  lat: z.number().optional(),
  lon: z.number().optional(),
  center: z.object({ lat: z.number(), lon: z.number() }).optional(),
  tags: z.record(z.string(), z.string()).optional(),
});
export type OsmElement = z.infer<typeof OsmElementSchema>;

export const OsmExtractSchema = z.object({ elements: z.array(OsmElementSchema) });
export type OsmExtract = z.infer<typeof OsmExtractSchema>;
